import React, { useRef, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { specialistSectorsSelectListNames } from 'lib/src/constants/enums';
import { metaData } from 'src/constants/meta';

import useForm from 'lib/src/hooks/useForm';
import useIsLoggedIn from 'lib/src/hooks/useIsLoggedIn';
import useShowMoreItems from 'src/hooks/useShowMoreItems';
import useAllSolutionsFetch from './hooks/useAllSolutionsFetch';
import useCaseStudiesToShow from './hooks/useCaseStudiesToShow';
import useCaseStudyOnSubmission from './hooks/useCaseStudyOnSubmission';

import ButtonRow from 'lib/src/components/button/ButtonRow';
import ActionButton from 'lib/src/components/button/ActionButton';
import MultiSelect from 'lib/src/components/form/MultiSelect';
import TextArea from 'lib/src/components/form/TextArea';

import DataCheck from '@components/dataCheck/DataCheck';
import FilterSubHeading from '@components/filterSubHeading/FilterSubHeading';
import NetworkingHubCaseStudyListCard from './NetworkingHubCaseStudyListCard';
import NetworkingHubSolutionSingle from '../single/NetworkingHubSolutionSingle';
import NetworkingHubSubmitSolutionModal from '../modals/NetworkingHubSubmitSolutionModal';
import ErrorModal from '@components/modals/ErrorModal';
import Meta from '@components/meta/Meta';

const NetworkingHubSolutionsList = () => {
    const scrollRef = useRef(null);
    const { id } = useParams();
    const [form, handleChange] = useForm({ selectedOptions: [], searchString: '' });
    const { solutions, isFetching, error } = useAllSolutionsFetch();
    const [amountToShow, showMoreItems] = useShowMoreItems(12, 12);
    const [caseStudiesToShow] = useCaseStudiesToShow(amountToShow, form, solutions);

    const [showSubmissionModal, setShowSubmissionModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);

    const { postError } = useCaseStudyOnSubmission(
        () => setShowSubmissionModal(false),
        () => setShowErrorModal(true),
        'solutions',
    );

    const isLoggedIn = useIsLoggedIn();

    useLayoutEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView();
        }
    }, []);

    if (id) return <NetworkingHubSolutionSingle />;

    return (
        <>
            <Meta data={metaData.networkingHubSolutions} />
            <div ref={scrollRef} />
            <FilterSubHeading
                id="solutions"
                title="Solutions"
                subText="Create or view case studies for successful battery related solutions"
            >
                <div className="filter-box-wrapper flex-row flex justify-evenly align-center flex-column-lm align-stretch-lm">
                    <div className="flex-row align-center flex-column-pt flex-4-pt align-stretch-pt">
                        <div className="flex-column hub-filters">
                            <MultiSelect
                                name="selectedOptions"
                                value={form.selectedOptions}
                                placeholder="Please select your filter tags"
                                options={specialistSectorsSelectListNames}
                                onChange={handleChange}
                                extraClasses="grey filter-bar column"
                                formFieldClasses="no-margin"
                            />
                            <TextArea
                                name="searchString"
                                onChange={handleChange}
                                placeholder="Search by title or summary"
                                extraClasses="grey filter-bar-search no-margin"
                            />
                        </div>
                        {isLoggedIn && (
                            <ActionButton
                                extraClasses="submit-button"
                                color="orange"
                                onClick={() => setShowSubmissionModal(true)}
                            >
                                Submit a solution
                            </ActionButton>
                        )}
                    </div>
                </div>
            </FilterSubHeading>

            <div className="container page-bottom-margin">
                <DataCheck
                    data={caseStudiesToShow.items}
                    isFetching={isFetching}
                    error={error}
                    noDataMessage="There are no solutions to display"
                >
                    <div className="flex-row flex-wrap">
                        {caseStudiesToShow.items.map(solution => (
                            <NetworkingHubCaseStudyListCard
                                key={solution.id}
                                item={solution}
                                urlPrefix="solutions"
                            />
                        ))}
                    </div>

                    {caseStudiesToShow.total > amountToShow && (
                        <ButtonRow>
                            <ActionButton color="grey" onClick={showMoreItems}>
                                Load more News Articles
                            </ActionButton>
                        </ButtonRow>
                    )}
                </DataCheck>

                {showSubmissionModal && (
                    <NetworkingHubSubmitSolutionModal
                        closeModal={() => setShowSubmissionModal(false)}
                    />
                )}

                {showErrorModal && (
                    <ErrorModal error={postError} closeModal={() => setShowErrorModal(false)} />
                )}
            </div>
        </>
    );
};

export default NetworkingHubSolutionsList;
